import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { SummarizerConfig } from '../../types/summarizer';

interface ConfigOverride {
  [key: string]: any;
}

interface ConfigStore {
  // State
  configs: Record<string, SummarizerConfig>;
  overrides: Record<string, ConfigOverride>;
  activeConfig: SummarizerConfig | null;
  isLoading: boolean;
  error: string | null;
  isDirty: boolean;

  // Actions
  loadConfigs: () => Promise<void>;
  loadConfig: (toolId: string) => Promise<SummarizerConfig | null>;
  saveConfig: (toolId: string, config: SummarizerConfig) => Promise<boolean>;
  deleteConfig: (toolId: string) => Promise<boolean>;
  setActiveConfig: (config: SummarizerConfig | null) => void;
  updateActiveConfig: (updates: Partial<SummarizerConfig>) => void;
  setOverride: (toolId: string, override: ConfigOverride) => void;
  clearOverride: (toolId: string) => void;
  applyOverrides: (config: SummarizerConfig, overrides?: ConfigOverride) => SummarizerConfig;
  resetToDefault: (toolId: string) => Promise<boolean>;
  exportConfig: (toolId: string) => string;
  importConfig: (jsonString: string) => Promise<boolean>;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setDirty: (dirty: boolean) => void;
}

// API URL from WordPress localization or fallback
const API_BASE = (window as any).tubeonai?.apiUrl || '/wp-json/tubeonai/v1';

// Get nonce with fallback
const getNonce = () => {
  const tubeonai = (window as any).tubeonai;
  return tubeonai?.nonce || tubeonai?.restNonce || '';
};

export const useConfigStore = create<ConfigStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        configs: {},
        overrides: {},
        activeConfig: null,
        isLoading: false,
        error: null,
        isDirty: false,

        // Load all configurations from API
        loadConfigs: async () => {
          set({ isLoading: true, error: null });

          try {
            const nonce = getNonce();
            const headers: HeadersInit = {
              'Content-Type': 'application/json',
            };

            // Only add nonce if available
            if (nonce) {
              headers['X-WP-Nonce'] = nonce;
            }

            const response = await fetch(`${API_BASE}/configs`, {
              method: 'GET',
              headers,
            });

            if (!response.ok) {
              throw new Error('Failed to load configurations');
            }

            const configs = await response.json();
            set({ configs, isLoading: false });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to load configurations',
              isLoading: false,
            });
          }
        },

        // Load specific configuration
        loadConfig: async (toolId: string) => {
          set({ isLoading: true, error: null });

          try {
            const nonce = getNonce();
            const headers: HeadersInit = {
              'Content-Type': 'application/json',
            };

            // Only add nonce if available
            if (nonce) {
              headers['X-WP-Nonce'] = nonce;
            }

            const response = await fetch(`${API_BASE}/configs/${toolId}`, {
              method: 'GET',
              headers,
            });

            if (!response.ok) {
              throw new Error(`Failed to load configuration for ${toolId}`);
            }

            const config = await response.json();

            // Apply any local overrides
            const { overrides } = get();
            const finalConfig = get().applyOverrides(config, overrides[toolId]);

            set((state) => ({
              configs: { ...state.configs, [toolId]: finalConfig },
              isLoading: false,
            }));

            return finalConfig;
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to load configuration',
              isLoading: false,
            });
            return null;
          }
        },

        // Save configuration to API
        saveConfig: async (toolId: string, config: SummarizerConfig) => {
          set({ isLoading: true, error: null });

          try {
            const nonce = getNonce();
            const headers: HeadersInit = {
              'Content-Type': 'application/json',
            };

            // Only add nonce if available (required for write operations)
            if (nonce) {
              headers['X-WP-Nonce'] = nonce;
            }

            const response = await fetch(`${API_BASE}/configs/${toolId}`, {
              method: 'POST',
              headers,
              body: JSON.stringify(config),
            });

            if (!response.ok) {
              throw new Error(`Failed to save configuration for ${toolId}`);
            }

            set((state) => ({
              configs: { ...state.configs, [toolId]: config },
              isLoading: false,
              isDirty: false,
            }));

            return true;
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to save configuration',
              isLoading: false,
            });
            return false;
          }
        },

        // Delete configuration (reset to default)
        deleteConfig: async (toolId: string) => {
          set({ isLoading: true, error: null });

          try {
            const nonce = getNonce();
            const headers: HeadersInit = {
              'Content-Type': 'application/json',
            };

            // Only add nonce if available (required for write operations)
            if (nonce) {
              headers['X-WP-Nonce'] = nonce;
            }

            const response = await fetch(`${API_BASE}/configs/${toolId}`, {
              method: 'DELETE',
              headers,
            });

            if (!response.ok) {
              throw new Error(`Failed to delete configuration for ${toolId}`);
            }

            // Reload the default config
            await get().loadConfig(toolId);

            set({ isLoading: false, isDirty: false });
            return true;
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to delete configuration',
              isLoading: false,
            });
            return false;
          }
        },

        // Set active configuration
        setActiveConfig: (config: SummarizerConfig | null) => {
          set({ activeConfig: config, isDirty: false });
        },

        // Update active configuration
        updateActiveConfig: (updates: Partial<SummarizerConfig>) => {
          const { activeConfig } = get();
          if (!activeConfig) return;

          const updatedConfig = {
            ...activeConfig,
            ...updates,
            updatedAt: new Date().toISOString(),
          };

          set({ activeConfig: updatedConfig, isDirty: true });
        },

        // Set override for a tool
        setOverride: (toolId: string, override: ConfigOverride) => {
          set((state) => ({
            overrides: {
              ...state.overrides,
              [toolId]: { ...state.overrides[toolId], ...override },
            },
          }));
        },

        // Clear override for a tool
        clearOverride: (toolId: string) => {
          set((state) => {
            const { [toolId]: _, ...rest } = state.overrides;
            return { overrides: rest };
          });
        },

        // Apply overrides to configuration
        applyOverrides: (config: SummarizerConfig, overrides?: ConfigOverride) => {
          if (!overrides) return config;
          return { ...config, ...overrides };

          // const deepMerge = (target: any, source: any): any => {
          //   const output = { ...target };

          //   Object.keys(source).forEach(key => {
          //     if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          //       if (target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
          //         output[key] = deepMerge(target[key], source[key]);
          //       } else {
          //         output[key] = source[key];
          //       }
          //     } else {
          //       output[key] = source[key];
          //     }
          //   });

          //   return output;
          // };

          // return deepMerge(config, overrides);
        },

        // Reset to default configuration
        resetToDefault: async (toolId: string) => {
          return get().deleteConfig(toolId);
        },

        // Export configuration as JSON
        exportConfig: (toolId: string) => {
          const { configs } = get();
          const config = configs[toolId];

          if (!config) {
            throw new Error(`Configuration for ${toolId} not found`);
          }

          return JSON.stringify(config, null, 2);
        },

        // Import configuration from JSON
        importConfig: async (jsonString: string) => {
          try {
            const config = JSON.parse(jsonString) as SummarizerConfig;

            if (!config.id) {
              throw new Error('Invalid configuration: missing ID');
            }

            return await get().saveConfig(config.id, config);
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to import configuration',
            });
            return false;
          }
        },

        // Set error message
        setError: (error: string | null) => {
          set({ error });
        },

        // Set loading state
        setLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },

        // Set dirty state
        setDirty: (dirty: boolean) => {
          set({ isDirty: dirty });
        },
      }),
      {
        name: 'tubeonai-config-store',
        partialize: (state) => ({
          overrides: state.overrides, // Only persist overrides
        }),
      }
    )
  )
);

// Helper hook to get config with overrides applied
export const useToolConfig = (toolId: string) => {
  const { configs, overrides, loadConfig, applyOverrides } = useConfigStore();

  const config = configs[toolId];
  const override = overrides[toolId];

  // Load config if not available
  if (!config) {
    loadConfig(toolId);
    return null;
  }

  return applyOverrides(config, override);
};
